import { useRef, useEffect, useLayoutEffect, useState, memo } from 'react';
import { Group, Rect, Circle, Ellipse, Text, Line } from 'react-konva';
import { TableConfig } from './TableConfigModal';
import Konva from 'konva';
import { guestSeatMultilineLabel, splitGuestNameIntoSeatLines } from '@/lib/seatLabelUtils';

interface RotationHandleProps {
  x: number;
  y: number;
  onRotate: (angle: number) => void;
  currentRotation: number;
  scale: number;
}

function RotationHandle({ x, y, onRotate, currentRotation, scale }: RotationHandleProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const handleRef = useRef<Konva.Group>(null);

  const handleDragMove = (e: Konva.KonvaEventObject<DragEvent>) => {
    e.cancelBubble = true;
    if (!isDragging) return;

    const stage = e.target.getStage();
    const group = e.target.getParent();
    if (!stage || !group) return;

    const pointerPos = stage.getPointerPosition();
    if (!pointerPos) return;

    // Get table position in stage coordinates
    const tablePos = group.getAbsolutePosition();
    
    // Calculate angle from table center to pointer
    const dx = pointerPos.x - tablePos.x;
    const dy = pointerPos.y - tablePos.y;
    const angleRad = Math.atan2(dy, dx);
    const angleDeg = (angleRad * 180) / Math.PI + 90; // Add 90 to adjust for handle position
    
    // Round to nearest 5 degrees
    const snappedAngle = Math.round(angleDeg / 5) * 5;
    
    onRotate(snappedAngle);
  };

  const handleDragStart = (e: Konva.KonvaEventObject<DragEvent>) => {
    e.cancelBubble = true;
    setIsDragging(true);
  };

  const handleDragEnd = (e: Konva.KonvaEventObject<DragEvent>) => {
    e.cancelBubble = true;
    setIsDragging(false);
  };

  const handleSize = 12 / scale;
  const lineLength = 30 / scale;

  return (
    <Group
      ref={handleRef}
      x={x}
      y={y}
      draggable
      onDragStart={handleDragStart}
      onDragMove={handleDragMove}
      onDragEnd={handleDragEnd}
      onMouseEnter={() => {
        setIsHovered(true);
        const stage = handleRef.current?.getStage();
        if (stage) {
          const container = stage.container();
          container.style.cursor = 'grab';
        }
      }}
      onMouseLeave={() => {
        setIsHovered(false);
        if (!isDragging) {
          const stage = handleRef.current?.getStage();
          if (stage) {
            const container = stage.container();
            container.style.cursor = 'default';
          }
        }
      }}
    >
      {/* Connection line */}
      <Line
        points={[0, 0, 0, -lineLength]}
        stroke="#3b82f6"
        strokeWidth={2 / scale}
        dash={[5 / scale, 5 / scale]}
      />
      
      {/* Rotation handle circle */}
      <Circle
        radius={handleSize}
        fill={isDragging ? '#2563eb' : isHovered ? '#3b82f6' : '#60a5fa'}
        stroke="#ffffff"
        strokeWidth={2 / scale}
        shadowBlur={isDragging ? 4 : 2}
        shadowOpacity={0.3}
        shadowColor="#000000"
      />
      
      {/* Rotation icon (spinner/circular arrow) */}
      <Circle
        radius={handleSize * 0.5}
        stroke="#ffffff"
        strokeWidth={2 / scale}
        dash={[handleSize * 0.8, handleSize * 0.4]}
        dashOffset={handleSize * 0.2}
      />
      {/* Arrow head */}
      <Line
        points={[
          handleSize * 0.35, -handleSize * 0.35,
          handleSize * 0.5, -handleSize * 0.15,
        ]}
        stroke="#ffffff"
        strokeWidth={2 / scale}
        lineCap="round"
      />
      <Line
        points={[
          handleSize * 0.35, -handleSize * 0.35,
          handleSize * 0.15, -handleSize * 0.5,
        ]}
        stroke="#ffffff"
        strokeWidth={2 / scale}
        lineCap="round"
      />
    </Group>
  );
}

interface ResizeHandleProps {
  x: number;
  y: number;
  onDragMove: (newWidth: number, newHeight: number, dx: number, dy: number) => void;
  onDragEnd: (newWidth: number, newHeight: number, dx: number, dy: number) => void;
  tableWidth: number;
  tableHeight: number;
  scale: number;
  position: 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w';
  lockAspectRatio?: boolean;
  constrainToAxis?: 'horizontal' | 'vertical' | 'both';
  tableRotation?: number;
}

function ResizeHandle({ 
  x, 
  y, 
  onDragMove, 
  onDragEnd, 
  tableWidth, 
  tableHeight, 
  scale, 
  position,
  lockAspectRatio = false,
  constrainToAxis = 'both',
  tableRotation = 0
}: ResizeHandleProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const [startSize, setStartSize] = useState({ width: 0, height: 0 });
  const handleRef = useRef<Konva.Rect>(null);

  const handleDragStart = (e: Konva.KonvaEventObject<DragEvent>) => {
    e.cancelBubble = true;
    setIsDragging(true);
    const stage = e.target.getStage();
    const pointerPos = stage?.getPointerPosition();
    if (pointerPos) {
      setStartPos(pointerPos);
      setStartSize({ width: tableWidth, height: tableHeight });
    }
    
    // Store original position for constrained handles
    const handle = e.target;
    if (constrainToAxis === 'horizontal' && (position === 'w' || position === 'e')) {
      handle.setAttr('originalY', y);
    }
  };

  const handleDragMove = (e: Konva.KonvaEventObject<DragEvent>) => {
    if (!isDragging) return;
    e.cancelBubble = true;
    
    const stage = e.target.getStage();
    const pointerPos = stage?.getPointerPosition();
    if (!pointerPos) return;
    
    // Calculate raw delta in screen space
    const rawDeltaX = (pointerPos.x - startPos.x) / scale;
    const rawDeltaY = (pointerPos.y - startPos.y) / scale;
    
    // Rotate delta to match table's orientation
    const rotRad = (tableRotation * Math.PI) / 180;
    const cos = Math.cos(-rotRad);
    const sin = Math.sin(-rotRad);
    const deltaX = rawDeltaX * cos - rawDeltaY * sin;
    const deltaY = rawDeltaX * sin + rawDeltaY * cos;
    
    // Apply axis constraints
    let constrainedDeltaX = deltaX;
    let constrainedDeltaY = deltaY;
    
    if (constrainToAxis === 'horizontal') {
      constrainedDeltaY = 0; // Only allow horizontal movement
    } else if (constrainToAxis === 'vertical') {
      constrainedDeltaX = 0; // Only allow vertical movement
    }
    
    let newWidth = startSize.width;
    let newHeight = startSize.height;
    
    // Calculate new dimensions based on handle position
    // Resize from the dragged side only (not from center)
    switch (position) {
      case 'nw': // Top-left corner
        newWidth = startSize.width - constrainedDeltaX;
        newHeight = startSize.height - constrainedDeltaY;
        break;
      case 'ne': // Top-right corner
        newWidth = startSize.width + constrainedDeltaX;
        newHeight = startSize.height - constrainedDeltaY;
        break;
      case 'se': // Bottom-right corner
        newWidth = startSize.width + constrainedDeltaX;
        newHeight = startSize.height + constrainedDeltaY;
        break;
      case 'sw': // Bottom-left corner
        newWidth = startSize.width - constrainedDeltaX;
        newHeight = startSize.height + constrainedDeltaY;
        break;
      case 'n': // Top edge
        newHeight = startSize.height - constrainedDeltaY;
        break;
      case 's': // Bottom edge
        newHeight = startSize.height + constrainedDeltaY;
        break;
      case 'e': // Right edge
        newWidth = startSize.width + constrainedDeltaX;
        break;
      case 'w': // Left edge
        newWidth = startSize.width - constrainedDeltaX;
        break;
    }
    
    // Calculate position adjustment to keep opposite side fixed
    // The table center needs to move by half the size change
    // When resizing from the right, the center moves right (positive dx)
    // When resizing from the left, the center moves left (negative dx)
    let dx = 0;
    let dy = 0;
    const widthChange = newWidth - startSize.width;
    const heightChange = newHeight - startSize.height;
    
    switch (position) {
      case 'nw': // Top-left: move center left and up (negative dx, negative dy)
        dx = -widthChange / 2;
        dy = -heightChange / 2;
        break;
      case 'ne': // Top-right: move center right and up (positive dx, negative dy)
        dx = widthChange / 2;
        dy = -heightChange / 2;
        break;
      case 'se': // Bottom-right: move center right and down (positive dx, positive dy)
        dx = widthChange / 2;
        dy = heightChange / 2;
        break;
      case 'sw': // Bottom-left: move center left and down (negative dx, positive dy)
        dx = -widthChange / 2;
        dy = heightChange / 2;
        break;
      case 'n': // Top: move center up (negative dy)
        dy = -heightChange / 2;
        break;
      case 's': // Bottom: move center down (positive dy)
        dy = heightChange / 2;
        break;
      case 'e': // Right: move center right (positive dx)
        dx = widthChange / 2;
        break;
      case 'w': // Left: move center left (negative dx)
        dx = -widthChange / 2;
        break;
    }
    
    // Rotate position adjustment from table's local coordinate system to stage coordinate system
    // The table's position is in stage coordinates, so we need to rotate the adjustment vector
    // Use positive rotation (opposite of delta rotation) to convert from table space to stage space
    const posRotRad = (tableRotation * Math.PI) / 180;
    const cosPosRot = Math.cos(posRotRad);
    const sinPosRot = Math.sin(posRotRad);
    const dxStage = dx * cosPosRot - dy * sinPosRot;
    const dyStage = dx * sinPosRot + dy * cosPosRot;
    dx = dxStage;
    dy = dyStage;
    
    // Snap to 5px increments
    newWidth = Math.round(newWidth / 10) * 10;
    newHeight = Math.round(newHeight / 10) * 10;
    
    // Apply minimum constraints after snapping
    newWidth = Math.max(80, newWidth);
    newHeight = Math.max(80, newHeight);
    
    // Lock aspect ratio if needed (for circular tables)
    if (lockAspectRatio) {
      const aspectRatio = startSize.width / startSize.height;
      if (position.includes('n') || position.includes('s')) {
        // Height changed, adjust width
        newWidth = Math.round((newHeight * aspectRatio) / 10) * 10;
      } else {
        // Width changed, adjust height
        newHeight = Math.round((newWidth / aspectRatio) / 10) * 10;
      }
    }
    
    // Apply maximum constraints
    newWidth = Math.min(newWidth, 800);
    newHeight = Math.min(newHeight, 800);
    
    // Constrain handle position for horizontal-only handles
    if (constrainToAxis === 'horizontal' && (position === 'w' || position === 'e')) {
      // Reset handle to original Y position
      const handle = e.target;
      const originalY = handle.getAttr('originalY') || y;
      handle.y(originalY);
    }
    
    onDragMove(newWidth, newHeight, dx, dy);
  };

  const handleDragEndFinal = (e: Konva.KonvaEventObject<DragEvent>) => {
    if (!isDragging) return;
    e.cancelBubble = true;
    
    const stage = e.target.getStage();
    const pointerPos = stage?.getPointerPosition();
    if (!pointerPos) {
      setIsDragging(false);
      return;
    }
    
    // Calculate raw delta in screen space
    const rawDeltaX = (pointerPos.x - startPos.x) / scale;
    const rawDeltaY = (pointerPos.y - startPos.y) / scale;
    
    // Rotate delta to match table's orientation
    const rotRad = (tableRotation * Math.PI) / 180;
    const cos = Math.cos(-rotRad);
    const sin = Math.sin(-rotRad);
    const deltaX = rawDeltaX * cos - rawDeltaY * sin;
    const deltaY = rawDeltaX * sin + rawDeltaY * cos;
    
    // Apply axis constraints
    let constrainedDeltaX = deltaX;
    let constrainedDeltaY = deltaY;
    
    if (constrainToAxis === 'horizontal') {
      constrainedDeltaY = 0; // Only allow horizontal movement
    } else if (constrainToAxis === 'vertical') {
      constrainedDeltaX = 0; // Only allow vertical movement
    }
    
    let newWidth = startSize.width;
    let newHeight = startSize.height;
    
    // Calculate final dimensions
    // Resize from the dragged side only (not from center)
    switch (position) {
      case 'nw':
        newWidth = startSize.width - constrainedDeltaX;
        newHeight = startSize.height - constrainedDeltaY;
        break;
      case 'ne':
        newWidth = startSize.width + constrainedDeltaX;
        newHeight = startSize.height - constrainedDeltaY;
        break;
      case 'se':
        newWidth = startSize.width + constrainedDeltaX;
        newHeight = startSize.height + constrainedDeltaY;
        break;
      case 'sw':
        newWidth = startSize.width - constrainedDeltaX;
        newHeight = startSize.height + constrainedDeltaY;
        break;
      case 'n':
        newHeight = startSize.height - constrainedDeltaY;
        break;
      case 's':
        newHeight = startSize.height + constrainedDeltaY;
        break;
      case 'e':
        newWidth = startSize.width + constrainedDeltaX;
        break;
      case 'w':
        newWidth = startSize.width - constrainedDeltaX;
        break;
    }
    
    // Calculate position adjustment to keep opposite side fixed
    let dx = 0;
    let dy = 0;
    const widthChange = newWidth - startSize.width;
    const heightChange = newHeight - startSize.height;
    
    switch (position) {
      case 'nw':
        dx = -widthChange / 2;
        dy = -heightChange / 2;
        break;
      case 'ne':
        dx = widthChange / 2;
        dy = -heightChange / 2;
        break;
      case 'se':
        dx = widthChange / 2;
        dy = heightChange / 2;
        break;
      case 'sw':
        dx = -widthChange / 2;
        dy = heightChange / 2;
        break;
      case 'n':
        dy = -heightChange / 2;
        break;
      case 's':
        dy = heightChange / 2;
        break;
      case 'e':
        dx = widthChange / 2;
        break;
      case 'w':
        dx = -widthChange / 2;
        break;
    }
    
    // Rotate position adjustment from table's local coordinate system to stage coordinate system
    // Use positive rotation (opposite of delta rotation) to convert from table space to stage space
    const cosPosRot = Math.cos(rotRad);
    const sinPosRot = Math.sin(rotRad);
    const dxStage = dx * cosPosRot - dy * sinPosRot;
    const dyStage = dx * sinPosRot + dy * cosPosRot;
    dx = dxStage;
    dy = dyStage;
    
    // Snap to 5px increments
    newWidth = Math.round(newWidth / 10) * 10;
    newHeight = Math.round(newHeight / 10) * 10;
    
    // Apply minimum constraints after snapping
    newWidth = Math.max(80, newWidth);
    newHeight = Math.max(80, newHeight);
    
    // Lock aspect ratio if needed
    if (lockAspectRatio) {
      const aspectRatio = startSize.width / startSize.height;
      if (position.includes('n') || position.includes('s')) {
        newWidth = Math.round((newHeight * aspectRatio) / 10) * 10;
      } else {
        newHeight = Math.round((newWidth / aspectRatio) / 10) * 10;
      }
    }
    
    // Apply maximum constraints
    newWidth = Math.min(newWidth, 800);
    newHeight = Math.min(newHeight, 800);
    
    // Constrain handle position for horizontal-only handles
    if (constrainToAxis === 'horizontal' && (position === 'w' || position === 'e')) {
      // Reset handle to original Y position
      const handle = e.target;
      const originalY = handle.getAttr('originalY') || y;
      handle.y(originalY);
    }
    
    onDragEnd(newWidth, newHeight, dx, dy);
    setIsDragging(false);
  };

  const getCursor = () => {
    switch (position) {
      case 'nw':
      case 'se':
        return 'nwse-resize';
      case 'ne':
      case 'sw':
        return 'nesw-resize';
      case 'n':
      case 's':
        return 'ns-resize';
      case 'e':
      case 'w':
        return 'ew-resize';
    }
  };

  const handleSize = 10 / scale;
  const handleVisualSize = Math.max(8, handleSize);

  return (
    <Rect
      ref={handleRef}
      x={x - handleVisualSize / 2}
      y={y - handleVisualSize / 2}
      width={handleVisualSize}
      height={handleVisualSize}
      fill={isDragging ? '#2563eb' : isHovered ? '#3b82f6' : '#60a5fa'}
      stroke="#ffffff"
      strokeWidth={2 / scale}
      cornerRadius={2 / scale}
      draggable
      onMouseEnter={() => {
        setIsHovered(true);
        const stage = handleRef.current?.getStage();
        if (stage) {
          const container = stage.container();
          container.style.cursor = getCursor();
        }
      }}
      onMouseLeave={() => {
        setIsHovered(false);
        if (!isDragging) {
          const stage = handleRef.current?.getStage();
          if (stage) {
            const container = stage.container();
            container.style.cursor = 'default';
          }
        }
      }}
      onDragStart={handleDragStart}
      onDragMove={handleDragMove}
      onDragEnd={handleDragEndFinal}
      shadowBlur={isDragging ? 4 : 2}
      shadowOpacity={0.3}
      shadowColor="#000000"
    />
  );
}

// Edge resize zone: makes the whole edge draggable for resizing
interface EdgeResizeZoneProps {
  tableWidth: number;
  tableHeight: number;
  scale: number;
  edge: 'n' | 's' | 'e' | 'w';
  onDragMove: (newWidth: number, newHeight: number, dx: number, dy: number) => void;
  onDragEnd: (newWidth: number, newHeight: number, dx: number, dy: number) => void;
  tableRotation?: number;
}

function EdgeResizeZone({ tableWidth, tableHeight, scale, edge, onDragMove, onDragEnd, tableRotation = 0 }: EdgeResizeZoneProps) {
  const zoneRef = useRef<Konva.Rect>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const [startSize, setStartSize] = useState({ width: 0, height: 0 });
  const thickness = 14 / scale;

  // Calculate cursor based on edge and table rotation
  const getCursor = () => {
    // Normalize rotation to 0-360
    const normalizedRotation = ((tableRotation % 360) + 360) % 360;
    
    // Determine effective direction after rotation
    // Each edge rotates: n->e->s->w->n
    const rotationSteps = Math.round(normalizedRotation / 45) % 8;
    
    // Base cursors for each edge
    const baseCursor = edge === 'n' || edge === 's' ? 'ns-resize' : 'ew-resize';
    
    // Map rotation to cursor types
    // 0° = original, 45° = diagonal, 90° = perpendicular, etc.
    const cursorMap: { [key: string]: string[] } = {
      'n': ['ns-resize', 'nesw-resize', 'ew-resize', 'nwse-resize', 'ns-resize', 'nesw-resize', 'ew-resize', 'nwse-resize'],
      's': ['ns-resize', 'nwse-resize', 'ew-resize', 'nesw-resize', 'ns-resize', 'nwse-resize', 'ew-resize', 'nesw-resize'],
      'e': ['ew-resize', 'nwse-resize', 'ns-resize', 'nesw-resize', 'ew-resize', 'nwse-resize', 'ns-resize', 'nesw-resize'],
      'w': ['ew-resize', 'nesw-resize', 'ns-resize', 'nwse-resize', 'ew-resize', 'nesw-resize', 'ns-resize', 'nwse-resize'],
    };
    
    return cursorMap[edge][rotationSteps];
  };

  const cursor = getCursor();

  const getZoneProps = () => {
    switch (edge) {
      case 'n':
        return { x: -tableWidth / 2, y: -tableHeight / 2 - thickness / 2, width: tableWidth, height: thickness };
      case 's':
        return { x: -tableWidth / 2, y: tableHeight / 2 - thickness / 2, width: tableWidth, height: thickness };
      case 'e':
        return { x: tableWidth / 2 - thickness / 2, y: -tableHeight / 2, width: thickness, height: tableHeight };
      case 'w':
        return { x: -tableWidth / 2 - thickness / 2, y: -tableHeight / 2, width: thickness, height: tableHeight };
    }
  };

  const handleDragStart = (e: Konva.KonvaEventObject<DragEvent>) => {
    e.cancelBubble = true;
    setIsDragging(true);
    const stage = e.target.getStage();
    const pointerPos = stage?.getPointerPosition();
    if (pointerPos) {
      setStartPos(pointerPos);
      setStartSize({ width: tableWidth, height: tableHeight });
    }
  };

  const handleDragMove = (e: Konva.KonvaEventObject<DragEvent>) => {
    if (!isDragging) return;
    e.cancelBubble = true;
    const stage = e.target.getStage();
    const pointerPos = stage?.getPointerPosition();
    if (!pointerPos) return;
    
    // Calculate raw delta in screen space
    const rawDeltaX = (pointerPos.x - startPos.x) / scale;
    const rawDeltaY = (pointerPos.y - startPos.y) / scale;
    
    // Rotate delta to match table's orientation
    const rotRad = (tableRotation * Math.PI) / 180;
    const cos = Math.cos(-rotRad);
    const sin = Math.sin(-rotRad);
    const deltaX = rawDeltaX * cos - rawDeltaY * sin;
    const deltaY = rawDeltaX * sin + rawDeltaY * cos;
    
    let newWidth = startSize.width;
    let newHeight = startSize.height;
    
    // Single-edge resize: snap to 0.1m (10cm) increments, resize from dragged side only
    if (edge === 'n') newHeight = Math.max(80, Math.round((startSize.height - deltaY) / 10) * 10);
    if (edge === 's') newHeight = Math.max(80, Math.round((startSize.height + deltaY) / 10) * 10);
    if (edge === 'e') newWidth = Math.max(80, Math.round((startSize.width + deltaX) / 10) * 10);
    if (edge === 'w') newWidth = Math.max(80, Math.round((startSize.width - deltaX) / 10) * 10);
    
    // Calculate position adjustment to keep opposite side fixed
    let dx = 0;
    let dy = 0;
    const widthChange = newWidth - startSize.width;
    const heightChange = newHeight - startSize.height;
    
    if (edge === 'n') dy = -heightChange / 2; // Top: move center up (negative dy)
    if (edge === 's') dy = heightChange / 2; // Bottom: move center down (positive dy)
    if (edge === 'e') dx = widthChange / 2; // Right: move center right (positive dx)
    if (edge === 'w') dx = -widthChange / 2; // Left: move center left (negative dx)
    
    // Rotate position adjustment from table's local coordinate system to stage coordinate system
    const posRotRad = (tableRotation * Math.PI) / 180;
    const cosPosRot = Math.cos(posRotRad);
    const sinPosRot = Math.sin(posRotRad);
    const dxStage = dx * cosPosRot - dy * sinPosRot;
    const dyStage = dx * sinPosRot + dy * cosPosRot;
    dx = dxStage;
    dy = dyStage;
    
    onDragMove(newWidth, newHeight, dx, dy);
  };

  const handleDragEnd = (e: Konva.KonvaEventObject<DragEvent>) => {
    if (!isDragging) return;
    e.cancelBubble = true;
    setIsDragging(false);
    const stage = e.target.getStage();
    const pointerPos = stage?.getPointerPosition();
    if (!pointerPos) return;
    
    // Calculate raw delta in screen space
    const rawDeltaX = (pointerPos.x - startPos.x) / scale;
    const rawDeltaY = (pointerPos.y - startPos.y) / scale;
    
    // Rotate delta to match table's orientation
    const rotRad = (tableRotation * Math.PI) / 180;
    const cos = Math.cos(-rotRad);
    const sin = Math.sin(-rotRad);
    const deltaX = rawDeltaX * cos - rawDeltaY * sin;
    const deltaY = rawDeltaX * sin + rawDeltaY * cos;
    
    let newWidth = startSize.width;
    let newHeight = startSize.height;
    
    if (edge === 'n') newHeight = Math.max(80, Math.round((startSize.height - deltaY) / 10) * 10);
    if (edge === 's') newHeight = Math.max(80, Math.round((startSize.height + deltaY) / 10) * 10);
    if (edge === 'e') newWidth = Math.max(80, Math.round((startSize.width + deltaX) / 10) * 10);
    if (edge === 'w') newWidth = Math.max(80, Math.round((startSize.width - deltaX) / 10) * 10);
    
    // Calculate position adjustment to keep opposite side fixed
    let dx = 0;
    let dy = 0;
    const widthChange = newWidth - startSize.width;
    const heightChange = newHeight - startSize.height;
    
    if (edge === 'n') dy = -heightChange / 2; // Top: move center up (negative dy)
    if (edge === 's') dy = heightChange / 2; // Bottom: move center down (positive dy)
    if (edge === 'e') dx = widthChange / 2; // Right: move center right (positive dx)
    if (edge === 'w') dx = -widthChange / 2; // Left: move center left (negative dx)
    
    // Rotate position adjustment from table's local coordinate system to stage coordinate system
    const posRotRad = (tableRotation * Math.PI) / 180;
    const cosPosRot = Math.cos(posRotRad);
    const sinPosRot = Math.sin(posRotRad);
    const dxStage = dx * cosPosRot - dy * sinPosRot;
    const dyStage = dx * sinPosRot + dy * cosPosRot;
    dx = dxStage;
    dy = dyStage;
    
    onDragEnd(newWidth, newHeight, dx, dy);
  };

  const zoneProps = getZoneProps();
  return (
    <Rect
      ref={zoneRef}
      {...zoneProps}
      fill="rgba(0,0,0,0.01)"
      draggable
      onDragStart={handleDragStart}
      onDragMove={handleDragMove}
      onDragEnd={handleDragEnd}
      onMouseEnter={() => {
        const stage = zoneRef.current?.getStage();
        if (stage) stage.container().style.cursor = cursor;
      }}
      onMouseLeave={() => {
        const stage = zoneRef.current?.getStage();
        if (stage) stage.container().style.cursor = 'default';
      }}
    />
  );
}

interface SeatControlsProps {
  x: number;
  y: number;
  currentSeats: number;
  onSeatsChange: (newSeats: number) => void;
  // New: adjust per-side seat counts from canvas controls
  onAdjustSide?: (side: 'top' | 'bottom' | 'left' | 'right' | 'leftEnd' | 'rightEnd', delta: number) => void;
  scale: number;
}

function SeatControls({ x, y, currentSeats, onSeatsChange, scale }: SeatControlsProps) {
  const buttonSize = 16 / scale;
  const fontSize = 12 / scale;
  
  const handleAddSeat = (e: Konva.KonvaEventObject<MouseEvent>) => {
    e.cancelBubble = true;
    onSeatsChange(currentSeats + 1);
  };
  
  const handleRemoveSeat = (e: Konva.KonvaEventObject<MouseEvent>) => {
    e.cancelBubble = true;
    if (currentSeats > 1) {
      onSeatsChange(currentSeats - 1);
    }
  };
  
  return (
    <Group x={x} y={y}>
      {/* Background */}
      <Rect
        x={-buttonSize * 2 - 4}
        y={-buttonSize / 2}
        width={buttonSize * 4 + 8}
        height={buttonSize + 4}
        fill="rgba(255, 255, 255, 0.9)"
        stroke="#d1d5db"
        strokeWidth={1}
        cornerRadius={4}
      />
      
      {/* Minus button */}
      <Rect
        x={-buttonSize * 2}
        y={-buttonSize / 2}
        width={buttonSize}
        height={buttonSize}
        fill="#ffffff"
        stroke="#9ca3af"
        strokeWidth={1}
        cornerRadius={2}
        onClick={handleRemoveSeat}
        onTap={handleRemoveSeat}
      />
      <Text
        text="-"
        x={-buttonSize * 2}
        y={-buttonSize / 2}
        width={buttonSize}
        height={buttonSize}
        fontSize={fontSize}
        fontStyle="bold"
        fill="#374151"
        align="center"
        verticalAlign="middle"
        onClick={handleRemoveSeat}
        onTap={handleRemoveSeat}
      />
      
      {/* Seat count */}
      <Text
        text={currentSeats.toString()}
        x={-buttonSize / 2}
        y={-buttonSize / 2}
        width={buttonSize}
        height={buttonSize}
        fontSize={fontSize}
        fontStyle="bold"
        fill="#374151"
        align="center"
        verticalAlign="middle"
      />
      
      {/* Plus button */}
      <Rect
        x={buttonSize}
        y={-buttonSize / 2}
        width={buttonSize}
        height={buttonSize}
        fill="#ffffff"
        stroke="#9ca3af"
        strokeWidth={1}
        cornerRadius={2}
        onClick={handleAddSeat}
        onTap={handleAddSeat}
      />
      <Text
        text="+"
        x={buttonSize}
        y={-buttonSize / 2}
        width={buttonSize}
        height={buttonSize}
        fontSize={fontSize}
        fontStyle="bold"
        fill="#374151"
        align="center"
        verticalAlign="middle"
        onClick={handleAddSeat}
        onTap={handleAddSeat}
      />
    </Group>
  );
}

const DEFAULT_SEAT_RADIUS = 28;
const DEFAULT_SEAT_HIGHLIGHT_PADDING = 4;
const DEFAULT_SEAT_NUMBER_SIZE = 24;
const DEFAULT_SEAT_NUMBER_FONT = 14;
const DEFAULT_INSIDE_OFFSET = 12;
const DEFAULT_SEAT_TEXT_WIDTH = 56;
const DEFAULT_SEAT_TEXT_HEIGHT = 12;

interface SeatToggleButtonProps {
  x: number;
  y: number;
  position: 'top' | 'bottom' | 'left' | 'right';
  isEnabled: boolean;
  onToggle: () => void;
  scale: number;
  tableRotation: number;
}

function SeatToggleButton({ x, y, position, isEnabled, onToggle, scale, tableRotation }: SeatToggleButtonProps) {
  const [isHovered, setIsHovered] = useState(false);
  const buttonRef = useRef<Konva.Group>(null);
  
  const buttonSize = 20 / scale;
  const iconSize = 12 / scale;
  
  const handleClick = (e: Konva.KonvaEventObject<MouseEvent>) => {
    e.cancelBubble = true;
    onToggle();
  };
  
  return (
    <Group
      ref={buttonRef}
      x={x}
      y={y}
      onClick={handleClick}
      onTap={handleClick}
      onMouseEnter={() => {
        setIsHovered(true);
        const stage = buttonRef.current?.getStage();
        if (stage) {
          const container = stage.container();
          container.style.cursor = 'pointer';
        }
      }}
      onMouseLeave={() => {
        setIsHovered(false);
        const stage = buttonRef.current?.getStage();
        if (stage) {
          const container = stage.container();
          container.style.cursor = 'default';
        }
      }}
    >
      {/* Button background */}
      <Circle
        radius={buttonSize / 2}
        fill={isEnabled ? (isHovered ? '#10b981' : '#059669') : (isHovered ? '#6b7280' : '#9ca3af')}
        stroke="#ffffff"
        strokeWidth={2 / scale}
        shadowBlur={2}
        shadowOpacity={0.3}
      />
      
      {/* Icon - check for enabled, X for disabled */}
      {isEnabled ? (
        // Checkmark icon
        <Group rotation={-tableRotation}>
          <Line
            points={[ -iconSize * 0.25, 0, -iconSize * 0.05, iconSize * 0.25 ]}
            stroke="#ffffff"
            strokeWidth={2 / scale}
            lineCap="round"
            lineJoin="round"
          />
          <Line
            points={[ -iconSize * 0.05, iconSize * 0.25, iconSize * 0.3, -iconSize * 0.2 ]}
            stroke="#ffffff"
            strokeWidth={2 / scale}
            lineCap="round"
            lineJoin="round"
          />
        </Group>
      ) : (
        // X icon
        <Group rotation={-tableRotation}>
          <Line
            points={[-iconSize / 3, -iconSize / 3, iconSize / 3, iconSize / 3]}
            stroke="#ffffff"
            strokeWidth={2 / scale}
            lineCap="round"
          />
          <Line
            points={[iconSize / 3, -iconSize / 3, -iconSize / 3, iconSize / 3]}
            stroke="#ffffff"
            strokeWidth={2 / scale}
            lineCap="round"
          />
        </Group>
      )}
    </Group>
  );
}

interface VisualTableProps {
  tableId: string;
  table: TableConfig;
  isSelected: boolean;
  highlightedRelatedGuests?: Set<string>;
  /** When true, position is held in local state during drag so parent does not re-render (smoother single-table drag). */
  useLocalDragPosition?: boolean;
  onSelect: (tableId: string, event?: MouseEvent | React.MouseEvent) => void;
  onDragStart: (tableId: string) => void;
  onDragMove?: (tableId: string, x: number, y: number) => void;
  onDragEnd: (tableId: string, x: number, y: number) => void;
  onDoubleClick: (tableId: string) => void;
  onSeatClick: (tableId: string, seatNumber: number) => void;
  onSeatDrop?: (tableId: string, seatNumber: number) => void;
  onSeatDragStart?: (tableId: string, seatNumber: number, guestId: string) => void;
  onResize: (tableId: string, width: number, height: number, dx: number, dy: number) => void;
  onSeatsChange: (tableId: string, newSeats: number) => void;
  onRotate?: (tableId: string, rotation: number) => void;
  onToggleSeatSide?: (tableId: string, side: 'top' | 'bottom' | 'left' | 'right') => void;
  onAdjustSide?: (tableId: string, side: 'top' | 'bottom' | 'left' | 'right' | 'leftEnd' | 'rightEnd', delta: number) => void;
  getGuestName: (rsvpId: string | null) => string | null;
  scale: number;
  /** Registers the Konva group for imperative multi-table drag (parent keeps a stable callback). */
  onTableGroupRef?: (tableId: string, node: Konva.Group | null) => void;
  /** When true, table and seat drop shadows are reduced while any table drag is active on the canvas. */
  reduceDropShadow?: boolean;
}

function VisualTable({
  tableId,
  table,
  isSelected,
  highlightedRelatedGuests = new Set(),
  useLocalDragPosition = false,
  onSelect,
  onDragStart,
  onDragMove,
  onDragEnd,
  onDoubleClick,
  onSeatClick,
  onSeatDrop,
  onSeatDragStart,
  onResize,
  onSeatsChange,
  onRotate,
  onToggleSeatSide,
  onAdjustSide,
  getGuestName,
  scale,
  onTableGroupRef,
  reduceDropShadow = false,
}: VisualTableProps) {
  const groupRef = useRef<Konva.Group>(null);
  const [tempSize, setTempSize] = useState<{ width: number; height: number } | null>(null);
  const [isRotating, setIsRotating] = useState(false);
  const [resizeStartPos, setResizeStartPos] = useState<{ x: number; y: number } | null>(null);
  const [dragPos, setDragPos] = useState<{ x: number; y: number } | null>(null);
  const dragRafRef = useRef<number | null>(null);
  const pendingDragPosRef = useRef<{ x: number; y: number } | null>(null);
  // Track drag state per seat (using Map to avoid hook violations)
  const seatDragStateRef = useRef<Map<number, { isDragging: boolean; dragStartPos: { x: number; y: number } | null }>>(new Map());

  const seatRadius = table.seatSize ?? DEFAULT_SEAT_RADIUS;
  const seatScale = seatRadius / DEFAULT_SEAT_RADIUS;
  const seatHighlightPadding = DEFAULT_SEAT_HIGHLIGHT_PADDING * seatScale;
  const seatNumberSize = DEFAULT_SEAT_NUMBER_SIZE * seatScale;
  const seatNumberFont = DEFAULT_SEAT_NUMBER_FONT * seatScale;
  const insideOffset = seatRadius + (DEFAULT_INSIDE_OFFSET * seatScale);
  const seatTextWidth = Math.min(
    DEFAULT_SEAT_TEXT_WIDTH * seatScale,
    seatRadius * 1.9
  );
  const seatTextHeight = Math.max(DEFAULT_SEAT_TEXT_HEIGHT * seatScale, seatRadius * 0.8);

  const isSeatRadius = (radius?: number) => radius !== undefined && Math.abs(radius - seatRadius) < 1;

  const stopEvent = (e: any) => {
    if (e?.evt?.preventDefault) e.evt.preventDefault();
    e.cancelBubble = true;
  };

  const handleDragStart = (e: Konva.KonvaEventObject<DragEvent>) => {
    // Don't drag table if clicking on a seat
    const target = e.target;

    // Check if the drag started on a seat element
    if (target.getType() === 'Circle') {
      const radius = target.getAttr('radius');
      if (isSeatRadius(radius)) {
        e.cancelBubble = true;
        return;
      }
    } else if (target.getType() === 'Text') {
      let parent: any = target.getParent();
      while (parent) {
        if (parent.getType() === 'Group') {
          const children = parent.getChildren();
          for (const child of children) {
            if (child.getType() === 'Circle' && isSeatRadius(child.getAttr('radius'))) {
              e.cancelBubble = true;
              return;
            }
          }
        }
        parent = parent.getParent();
      }
    }

    if (useLocalDragPosition) {
      setDragPos({ x: table.x, y: table.y });
    }
    onDragStart(tableId);
  };

  const handleDragMove = (e: Konva.KonvaEventObject<DragEvent>) => {
    const group = e.target;
    if (useLocalDragPosition) {
      pendingDragPosRef.current = { x: group.x(), y: group.y() };
      if (dragRafRef.current === null) {
        dragRafRef.current = requestAnimationFrame(() => {
          dragRafRef.current = null;
          if (pendingDragPosRef.current) {
            setDragPos(pendingDragPosRef.current);
          }
        });
      }
    } else if (onDragMove) {
      onDragMove(tableId, group.x(), group.y());
    }
  };

  const handleDragEnd = (e: Konva.KonvaEventObject<DragEvent>) => {
    const group = e.target;
    const snapIncrement = 10;
    const snappedX = Math.round(group.x() / snapIncrement) * snapIncrement;
    const snappedY = Math.round(group.y() / snapIncrement) * snapIncrement;
    group.x(snappedX);
    group.y(snappedY);
    if (dragRafRef.current !== null) {
      cancelAnimationFrame(dragRafRef.current);
      dragRafRef.current = null;
    }
    pendingDragPosRef.current = null;
    if (useLocalDragPosition) {
      setDragPos(null);
    }
    onDragEnd(tableId, snappedX, snappedY);
  };

  const handleResizeDragMove = (newWidth: number, newHeight: number, dx: number, dy: number) => {
    // Track starting position on first move (when tempSize is null)
    if (resizeStartPos === null && tempSize === null) {
      setResizeStartPos({ x: table.x, y: table.y });
    }
    setTempSize({ width: newWidth, height: newHeight });
    // Apply position adjustment during resize relative to starting position
    const startPos = resizeStartPos || { x: table.x, y: table.y };
    if (groupRef.current) {
      groupRef.current.x(startPos.x + dx);
      groupRef.current.y(startPos.y + dy);
    }
  };

  const handleResizeDragEnd = (newWidth: number, newHeight: number, dx: number, dy: number) => {
    setTempSize(null);
    // Apply final position adjustment relative to starting position
    const startPos = resizeStartPos || { x: table.x, y: table.y };
    const newX = startPos.x + dx;
    const newY = startPos.y + dy;
    setResizeStartPos(null);
    onResize(tableId, newWidth, newHeight, newX, newY);
  };

  const handleRotationChange = (angle: number) => {
    setIsRotating(true);
    if (onRotate) {
      onRotate(tableId, angle);
    }
  };

  useEffect(() => {
    return () => {
      if (dragRafRef.current !== null) {
        cancelAnimationFrame(dragRafRef.current);
      }
    };
  }, []);

  useLayoutEffect(() => {
    if (!onTableGroupRef) return;
    onTableGroupRef(tableId, groupRef.current);
    return () => {
      onTableGroupRef(tableId, null);
    };
  }, [tableId, onTableGroupRef]);

  useEffect(() => {
    if (isRotating) {
      // Clear rotating state after a short delay
      const timer = setTimeout(() => setIsRotating(false), 100);
      return () => clearTimeout(timer);
    }
  }, [table.rotation, isRotating]);

  const renderTableShape = () => {
    const { type, color } = table;
    const width = tempSize?.width ?? table.width;
    const height = tempSize?.height ?? table.height;
    const strokeColor = isSelected ? '#3b82f6' : '#4a5568';
    const strokeWidth = isSelected ? 3 : 1;
    const isResizing = tempSize !== null;
    const opacity = isResizing ? 0.7 : 1;
    const shapeShadowBlur = reduceDropShadow ? 0 : 5;

    switch (type) {
      case 'circular':
        // Use minimum of width and height to ensure circle stays circular
        const radius = Math.min(width, height) / 2;
        return (
          <Circle
            radius={radius}
            fill={color}
            stroke={strokeColor}
            strokeWidth={strokeWidth}
            shadowBlur={shapeShadowBlur}
            shadowOpacity={reduceDropShadow ? 0 : 0.3}
            opacity={opacity}
          />
        );

      case 'oval':
        return (
          <Ellipse
            radiusX={width / 2}
            radiusY={height / 2}
            fill={color}
            stroke={strokeColor}
            strokeWidth={strokeWidth}
            shadowBlur={shapeShadowBlur}
            shadowOpacity={reduceDropShadow ? 0 : 0.3}
            opacity={opacity}
          />
        );

      case 'square':
      case 'rectangular':
        return (
          <Rect
            x={-width / 2}
            y={-height / 2}
            width={width}
            height={height}
            fill={color}
            stroke={strokeColor}
            strokeWidth={strokeWidth}
            cornerRadius={0}
            shadowBlur={shapeShadowBlur}
            shadowOpacity={reduceDropShadow ? 0 : 0.3}
            opacity={opacity}
          />
        );

      default:
        return (
          <Rect
            x={-width / 2}
            y={-height / 2}
            width={width}
            height={height}
            fill={color}
            stroke={strokeColor}
            strokeWidth={strokeWidth}
            opacity={opacity}
          />
        );
    }
  };


  const renderSeats = () => {
    return table.seating
      .filter((seat) => seat && seat.seatNumber !== undefined)
      .map((seat) => {
        const guestName = getGuestName(seat.rsvpId);
        const isOccupied = !!guestName;
        const defaultChairColor = table.chairColor || '#e5e7eb';
        const isRelatedGuest = seat.rsvpId && highlightedRelatedGuests.has(seat.rsvpId);
        // Use bright yellow for related guests
        const chairColor = isRelatedGuest ? '#facc15' : (isOccupied ? '#10b981' : defaultChairColor);
        const strokeColor = isRelatedGuest ? '#eab308' : (isOccupied ? '#059669' : '#9ca3af');

        // Initialize drag state for this seat if not exists
        if (!seatDragStateRef.current.has(seat.seatNumber)) {
          seatDragStateRef.current.set(seat.seatNumber, {
            isDragging: false,
            dragStartPos: null,
          } as any);
        }
        // Initialize isClicking flag if not exists
        if (!(seatDragStateRef.current.get(seat.seatNumber) as any)?.isClicking) {
          (seatDragStateRef.current.get(seat.seatNumber) as any).isClicking = false;
        }
        const dragState = seatDragStateRef.current.get(seat.seatNumber)!;

        // Handle mouse down to start drag
        const handleMouseDown = (e: Konva.KonvaEventObject<MouseEvent>) => {
          if (!isOccupied || !seat.rsvpId || !onSeatDragStart) return;
          
          e.cancelBubble = true;
          e.evt.stopPropagation();
          // Don't prevent default - allow click to work if no drag occurs
          
          const stage = e.target.getStage();
          if (!stage) return;
          
          const pointerPos = stage.getPointerPosition();
          if (!pointerPos) return;
          
          // Store initial mouse position and stage reference
          dragState.dragStartPos = { x: pointerPos.x, y: pointerPos.y };
          const stageRef = stage;
          let hasMoved = false;
          
          // Set up global mouse move and up handlers
          const handleGlobalMouseMove = (evt: MouseEvent) => {
            if (!dragState.dragStartPos) return;
            
            // Don't start drag if we're in click mode
            if ((dragState as any).isClicking) {
              return;
            }
            
            // Check if mouse moved enough to start drag (threshold)
            const container = stageRef.container();
            const rect = container.getBoundingClientRect();
            const x = evt.clientX - rect.left;
            const y = evt.clientY - rect.top;
            
            const dx = x - dragState.dragStartPos.x;
            const dy = y - dragState.dragStartPos.y;
            const distance = Math.sqrt(dx * dx + dy * dy);
            
            if (distance > 10 && !dragState.isDragging && seat.rsvpId) {
              // Drag has started - trigger drag start
              hasMoved = true;
              dragState.isDragging = true;
              onSeatDragStart(tableId, seat.seatNumber, seat.rsvpId);
            }
          };
          
          const cleanup = () => {
            window.removeEventListener('mousemove', handleGlobalMouseMove);
            window.removeEventListener('mouseup', handleGlobalMouseUp);
            
            // Only clear drag state if drag actually started
            if (dragState.isDragging) {
              // Drag was in progress, let it complete
            } else {
              // No drag occurred, allow click to proceed
              dragState.dragStartPos = null;
            }
          };
          
          const handleGlobalMouseUp = () => {
            cleanup();
          };
          
          // Store cleanup function so we can call it from click handler
          (dragState as any).cleanup = cleanup;
          
          window.addEventListener('mousemove', handleGlobalMouseMove);
          window.addEventListener('mouseup', handleGlobalMouseUp);
        };

        return (
          <Group
            key={`seat-${seat.seatNumber}-${seat.rsvpId || 'empty'}`}
            x={seat.x}
            y={seat.y}
            draggable={false}
            listening={true}
            onClick={(e: any) => {
              e.cancelBubble = true;
              if (e.evt) {
                e.evt.stopPropagation();
              }
              // Only prevent default if we're actually dragging
              if (dragState.isDragging) {
                if (e.evt) {
                  e.evt.preventDefault();
                }
                return;
              }
              
              // Mark as clicking to prevent drag from starting
              (dragState as any).isClicking = true;
              
              // Clean up any pending drag handlers immediately when clicking
              if (dragState.dragStartPos && (dragState as any).cleanup) {
                (dragState as any).cleanup();
                dragState.dragStartPos = null;
                delete (dragState as any).cleanup;
              }
              
              // Normal click - allow it to proceed
              onSeatClick(tableId, seat.seatNumber);
              
              // Clear clicking flag after a short delay
              setTimeout(() => {
                (dragState as any).isClicking = false;
              }, 100);
            }}
            onTap={(e: any) => {
              e.cancelBubble = true;
              if (e.evt) {
                e.evt.stopPropagation();
              }
              // Only prevent default if we're actually dragging
              if (dragState.isDragging) {
                if (e.evt) {
                  e.evt.preventDefault();
                }
                return;
              }
              
              // Mark as clicking to prevent drag from starting
              (dragState as any).isClicking = true;
              
              // Clean up any pending drag handlers immediately when tapping
              if (dragState.dragStartPos && (dragState as any).cleanup) {
                (dragState as any).cleanup();
                dragState.dragStartPos = null;
                delete (dragState as any).cleanup;
              }
              
              // Normal tap - allow it to proceed
              onSeatClick(tableId, seat.seatNumber);
              
              // Clear clicking flag after a short delay
              setTimeout(() => {
                (dragState as any).isClicking = false;
              }, 100);
            }}
            onMouseDown={(e: any) => {
              // Prevent table drag when clicking on seat
              e.cancelBubble = true;
              if (e.evt) {
                e.evt.stopPropagation();
                // Don't prevent default - allow click to work normally
              }
              // Only set up drag handlers if occupied, but don't start drag yet
              // Drag will only start if mouse moves beyond threshold
              if (isOccupied && seat.rsvpId && onSeatDragStart) {
                handleMouseDown(e);
              }
            }}
            // Note: Konva drag events don't interfere with HTML5 drag and drop
            // HTML5 drags from sidebar are handled by the stage drop handler
            // These handlers are only for Konva-based seat-to-seat drags
            onDragOver={(e: any) => {
              // Only handle if this is a Konva drag, not HTML5 drag
              if (e.evt.dataTransfer?.effectAllowed === 'move' && dragState.isDragging) {
                const evt = e.evt;
                evt.preventDefault();
                evt.stopPropagation();
                if (evt.dataTransfer) {
                  evt.dataTransfer.dropEffect = 'move';
                }
              }
            }}
            onDragEnter={(e: any) => {
              // Only handle if this is a Konva drag, not HTML5 drag
              if (e.evt.dataTransfer?.effectAllowed === 'move' && dragState.isDragging) {
                const evt = e.evt;
                evt.preventDefault();
                evt.stopPropagation();
                if (evt.dataTransfer) {
                  evt.dataTransfer.dropEffect = 'move';
                }
              }
            }}
            onDrop={(e: any) => {
              // Only handle Konva drag drops (seat-to-seat), not HTML5 drags from sidebar
              // HTML5 drags from sidebar will be handled by the stage drop handler
              if (dragState.isDragging && onSeatDrop) {
                const evt = e.evt;
                evt.preventDefault();
                evt.stopPropagation();
                onSeatDrop(tableId, seat.seatNumber);
              }
            }}
            onMouseEnter={(e: any) => {
              const stage = e.target.getStage();
              if (stage) {
                const container = stage.container();
                container.style.cursor = isOccupied ? 'grab' : 'pointer';
              }
            }}
            onMouseLeave={(e: any) => {
              const stage = e.target.getStage();
              if (stage) {
                const container = stage.container();
                container.style.cursor = 'default';
              }
              // Clear drag state for this seat
              if (seatDragStateRef.current.has(seat.seatNumber)) {
                const state = seatDragStateRef.current.get(seat.seatNumber)!;
                state.isDragging = false;
                state.dragStartPos = null;
              }
            }}
          >
            {/* Chair */}
            <Circle
              radius={seatRadius}
              fill={chairColor}
              stroke={strokeColor}
              strokeWidth={isRelatedGuest ? 4 : 2}
              shadowBlur={
                reduceDropShadow ? 0 : isRelatedGuest ? 10 : 3
              }
              shadowOpacity={reduceDropShadow ? 0 : isRelatedGuest ? 0.6 : 0.3}
              shadowColor={isRelatedGuest ? '#eab308' : '#000000'}
            />
            {/* Highlight ring for related guests */}
            {isRelatedGuest && (
              <Circle
                radius={seatRadius + seatHighlightPadding}
                fill="transparent"
                stroke="#facc15"
                strokeWidth={2}
                opacity={0.6}
              />
            )}
            
            {/* Guest name inside chair (if occupied); one line per word */}
            {guestName && (() => {
              const trimmed = guestName.trim();
              const nameParts = splitGuestNameIntoSeatLines(trimmed);
              const displayText = guestSeatMultilineLabel(trimmed);
              const maxW = seatRadius * 1.82;
              const maxTotalH = seatRadius * 1.72;
              const lineCount = Math.max(1, nameParts.length);
              const minFs = Math.max(3.5, 4 * seatScale);
              let guestFontSize = Math.min(10 * seatScale, seatRadius * 0.36);
              while (guestFontSize >= minFs) {
                const lineH = guestFontSize * 1.18;
                const totalH = lineCount * lineH;
                const charW = guestFontSize * 0.58;
                const longestToken =
                  nameParts.length > 0
                    ? Math.max(...nameParts.map((p) => p.length))
                    : trimmed.length;
                const wMax = longestToken * charW;
                if (wMax <= maxW && totalH <= maxTotalH) {
                  break;
                }
                guestFontSize -= 0.5;
              }

              return (
                <Group rotation={-table.rotation}>
                  <Text
                    text={displayText}
                    fontSize={guestFontSize}
                    lineHeight={guestFontSize * 1.18}
                    fill="#000000"
                    fontStyle="bold"
                    align="center"
                    verticalAlign="middle"
                    width={seatTextWidth}
                    height={seatTextHeight}
                    offsetX={seatTextWidth / 2}
                    offsetY={seatTextHeight / 2}
                  />
                </Group>
              );
            })()}
          </Group>
        );
      });
  };

  // Render seat numbers on the table surface
  const renderSeatNumbers = () => {
    return table.seating
      .filter((seat) => seat && seat.seatNumber !== undefined)
      .map((seat) => {
        let numberX = seat.x;
        let numberY = seat.y;

        if (table.type === 'circular' || table.type === 'oval') {
          const distance = Math.sqrt(seat.x * seat.x + seat.y * seat.y);
          if (distance > 0) {
            const directionInX = -seat.x / distance;
            const directionInY = -seat.y / distance;
            numberX = seat.x + (directionInX * insideOffset);
            numberY = seat.y + (directionInY * insideOffset);
          }
        } else {
          const tableHalfWidth = table.width / 2;
          const tableHalfHeight = table.height / 2;

          const distanceToTop = Math.abs(seat.y + tableHalfHeight);
          const distanceToBottom = Math.abs(seat.y - tableHalfHeight);
          const distanceToLeft = Math.abs(seat.x + tableHalfWidth);
          const distanceToRight = Math.abs(seat.x - tableHalfWidth);

          const minDistance = Math.min(distanceToTop, distanceToBottom, distanceToLeft, distanceToRight);

          if (minDistance === distanceToTop) {
            numberX = seat.x;
            numberY = seat.y + insideOffset;
          } else if (minDistance === distanceToBottom) {
            numberX = seat.x;
            numberY = seat.y - insideOffset;
          } else if (minDistance === distanceToLeft) {
            numberX = seat.x + insideOffset;
            numberY = seat.y;
          } else {
            numberX = seat.x - insideOffset;
            numberY = seat.y;
          }
        }

        return (
          <Group key={`seat-label-${seat.seatNumber}`} listening={false}>
            <Text
              x={numberX}
              y={numberY}
              text={seat.seatNumber.toString()}
              fontSize={seatNumberFont}
              fill="#000000"
              fontStyle="bold"
              align="center"
              verticalAlign="middle"
              width={seatNumberSize}
              height={seatNumberSize}
              offsetX={seatNumberSize / 2}
              offsetY={seatNumberSize / 2}
              rotation={-table.rotation}
            />
          </Group>
        );
      });
  };

  // Render guest names in the center of the table
  // Guest names are now only shown on the seat icons, not on the table surface
  // This function is kept for potential future use but returns null
  const renderGuestNames = () => {
    return null;
  };

  const renderTableLabel = () => {
    const fontSize = 14 / scale;
    const width = tempSize?.width ?? table.width;
    
    // Center the table name both horizontally and vertically for all table types
    const textWidth = width * 0.8;
    
    return (
      <Group listening={false}>
        <Text
          text={table.name}
          fontSize={fontSize}
          fontStyle="bold"
          fill="#1f2937"
          align="center"
          verticalAlign="middle"
          x={0}
          y={0}
          width={textWidth}
          offsetX={textWidth / 2}
          offsetY={fontSize / 2}
          rotation={-table.rotation}
        />
      </Group>
    );
  };

  const renderDimensionIndicator = () => {
    if (!tempSize) return null;

    const fontSize = 12 / scale;
    const padding = 8 / scale;
    const width = tempSize.width;
    const height = tempSize.height;
    
    // Convert pixels to meters (100px = 1m)
    const widthM = (width / 100).toFixed(1);
    const heightM = (height / 100).toFixed(1);
    const text = `${widthM}m × ${heightM}m`;
    const textWidth = text.length * fontSize * 0.6;

    return (
      <Group>
        {/* Background */}
        <Rect
          x={-textWidth / 2 - padding}
          y={-height / 2 - 40 / scale}
          width={textWidth + padding * 2}
          height={fontSize + padding * 2}
          fill="rgba(255, 255, 255, 0.95)"
          stroke="#d1d5db"
          strokeWidth={1 / scale}
          cornerRadius={4 / scale}
          shadowBlur={2}
          shadowOpacity={0.3}
        />
        {/* Dimension text */}
        <Text
          text={text}
          x={-textWidth / 2}
          y={-height / 2 - 40 / scale + padding}
          fontSize={fontSize}
          fontStyle="bold"
          fill="#000000"
          align="center"
          rotation={-table.rotation}
        />
      </Group>
    );
  };

  const renderRotationIndicator = () => {
    if (!isRotating) return null;

    const fontSize = 12 / scale;
    const padding = 8 / scale;
    const width = tempSize?.width ?? table.width;
    const height = tempSize?.height ?? table.height;
    
    // Normalize angle to 0-360
    let displayAngle = table.rotation % 360;
    if (displayAngle < 0) displayAngle += 360;
    
    const text = `${Math.round(displayAngle)}°`;
    const textWidth = text.length * fontSize * 0.6;
    const yOffset = tempSize ? -60 / scale : -40 / scale; // Adjust position if size indicator is also showing

    return (
      <Group>
        {/* Background */}
        <Rect
          x={-textWidth / 2 - padding}
          y={-height / 2 + yOffset}
          width={textWidth + padding * 2}
          height={fontSize + padding * 2}
          fill="rgba(139, 92, 246, 0.95)"
          cornerRadius={4 / scale}
          shadowBlur={2}
          shadowOpacity={0.3}
        />
        {/* Rotation angle text */}
        <Text
          text={text}
          x={-textWidth / 2}
          y={-height / 2 + yOffset + padding}
          fontSize={fontSize}
          fontStyle="bold"
          fill="#ffffff"
          align="center"
          rotation={-table.rotation}
        />
      </Group>
    );
  };

  const renderRotationHandle = () => {
    if (!isSelected) return null;

    const width = tempSize?.width ?? table.width;
    const height = tempSize?.height ?? table.height;
    
    return (
      <RotationHandle
        x={0}
        y={-height / 2 - 55 / scale}
        onRotate={handleRotationChange}
        currentRotation={table.rotation}
        scale={scale}
      />
    );
  };

  const renderResizeHandles = () => {
    if (!isSelected) return null;

    const width = tempSize?.width ?? table.width;
    const height = tempSize?.height ?? table.height;
    
    // For circular tables, use a circular drag handle
    if (table.type === 'circular') {
      const radius = width / 2;
      const handleSize = 12 / scale;
    
    return (
      <Group>
          {/* Four cardinal direction resize handles on the circle */}
          {[0, 90, 180, 270].map((angle) => {
            const rad = (angle * Math.PI) / 180;
            const x = Math.cos(rad) * radius;
            const y = Math.sin(rad) * radius;
            
            return (
              <Circle
                key={angle}
                x={x}
                y={y}
                radius={handleSize}
                fill="#3b82f6"
                stroke="#ffffff"
                strokeWidth={2 / scale}
                draggable
                onDragStart={(e) => {
                  e.cancelBubble = true;
                  setResizeStartPos({ x: table.x, y: table.y });
                  setTempSize({ width: table.width, height: table.height });
                }}
                onDragMove={(e) => {
                  e.cancelBubble = true;
                  const stage = e.target.getStage();
                  if (!stage) return;
                  
                  const pointerPos = stage.getPointerPosition();
                  if (!pointerPos) return;
                  
                  const group = e.target.getParent()?.getParent();
                  if (!group) return;
                  
                  const tablePos = group.getAbsolutePosition();
                  
                  // Calculate distance from table center to pointer
                  const dx = pointerPos.x - tablePos.x;
                  const dy = pointerPos.y - tablePos.y;
                  const distance = Math.sqrt(dx * dx + dy * dy);
                  
                  // New diameter = distance * 2, snap to 10 unit increments
                  let newDiameter = Math.round((distance * 2) / 10) * 10;
                  newDiameter = Math.max(80, newDiameter); // Min diameter 80
                  
                  // For circular tables, resize from center (no position adjustment)
                  handleResizeDragMove(newDiameter, newDiameter, 0, 0);
                }}
                onDragEnd={(e) => {
                  e.cancelBubble = true;
                  if (tempSize) {
                    // For circular tables, resize from center (no position adjustment)
                    handleResizeDragEnd(tempSize.width, tempSize.height, 0, 0);
                  }
                  setTempSize(null);
                  setResizeStartPos(null);
                }}
                onMouseEnter={() => {
                  const stage = groupRef.current?.getStage();
                  if (stage) {
                    const container = stage.container();
                    container.style.cursor = 'nwse-resize';
                  }
                }}
                onMouseLeave={() => {
                  const stage = groupRef.current?.getStage();
                  if (stage) {
                    const container = stage.container();
                    container.style.cursor = 'default';
                  }
                }}
              />
            );
          })}
        </Group>
      );
    }
    
    // For rectangular and square tables, use edge resize zones
    return (
      <Group>
        <EdgeResizeZone tableWidth={table.width} tableHeight={table.height} scale={scale} edge="n" onDragMove={handleResizeDragMove} onDragEnd={handleResizeDragEnd} tableRotation={table.rotation} />
        <EdgeResizeZone tableWidth={table.width} tableHeight={table.height} scale={scale} edge="s" onDragMove={handleResizeDragMove} onDragEnd={handleResizeDragEnd} tableRotation={table.rotation} />
        <EdgeResizeZone tableWidth={table.width} tableHeight={table.height} scale={scale} edge="w" onDragMove={handleResizeDragMove} onDragEnd={handleResizeDragEnd} tableRotation={table.rotation} />
        <EdgeResizeZone tableWidth={table.width} tableHeight={table.height} scale={scale} edge="e" onDragMove={handleResizeDragMove} onDragEnd={handleResizeDragEnd} tableRotation={table.rotation} />
      </Group>
    );
  };

  const renderSeatToggleButtons = () => {
    if (!isSelected) return null;
    
    // For circular tables, show simple +/- controls to add/remove seats
    if (table.type === 'circular' && onAdjustSide) {
      const width = tempSize?.width ?? table.width;
      const buttonSize = 14 / scale;
      const yOffset = width / 2 + 25 / scale; // Below the table
      
      return (
        <Group
          x={0}
          y={yOffset}
          onMouseDown={stopEvent}
          onTouchStart={stopEvent}
          onClick={stopEvent}
          onTap={stopEvent}
          onDblClick={stopEvent}
          onDblTap={stopEvent}
        >
          <Rect
            x={-buttonSize * 1.2}
            y={-buttonSize / 2}
            width={buttonSize * 2.4}
            height={buttonSize}
            fill="rgba(255,255,255,0.95)"
            stroke="#d1d5db"
            cornerRadius={3 / scale}
            onMouseDown={stopEvent}
            onTouchStart={stopEvent}
            onDblClick={stopEvent}
            onDblTap={stopEvent}
          />
          <Text
            text="-"
            x={-buttonSize * 1.1}
            y={-buttonSize / 2}
            width={buttonSize}
            height={buttonSize}
            align="center"
            verticalAlign="middle"
            fontSize={11 / scale}
            onMouseEnter={() => {
              const stage = groupRef.current?.getStage();
              if (stage) stage.container().style.cursor = 'pointer';
            }}
            onMouseLeave={() => {
              const stage = groupRef.current?.getStage();
              if (stage) stage.container().style.cursor = 'default';
            }}
            onClick={(e) => {
              stopEvent(e);
              onAdjustSide && onAdjustSide(tableId, 'top', -1); // Use 'top' as a generic side for circular
            }}
            onTap={(e) => {
              stopEvent(e);
              onAdjustSide && onAdjustSide(tableId, 'top', -1);
            }}
            onDblClick={stopEvent}
            onDblTap={stopEvent}
          />
          <Text
            text="+"
            x={buttonSize * 0.1}
            y={-buttonSize / 2}
            width={buttonSize}
            height={buttonSize}
            align="center"
            verticalAlign="middle"
            fontSize={11 / scale}
            onMouseEnter={() => {
              const stage = groupRef.current?.getStage();
              if (stage) stage.container().style.cursor = 'pointer';
            }}
            onMouseLeave={() => {
              const stage = groupRef.current?.getStage();
              if (stage) stage.container().style.cursor = 'default';
            }}
            onClick={(e) => {
              stopEvent(e);
              onAdjustSide && onAdjustSide(tableId, 'top', +1);
            }}
            onTap={(e) => {
              stopEvent(e);
              onAdjustSide && onAdjustSide(tableId, 'top', +1);
            }}
            onDblClick={stopEvent}
            onDblTap={stopEvent}
          />
        </Group>
      );
    }
    
    if (!onToggleSeatSide) return null;
    if (table.type !== 'rectangular' && table.type !== 'square') return null;

    const width = tempSize?.width ?? table.width;
    const height = tempSize?.height ?? table.height;
    // Place buttons inside the table by this inset
    const insetFromEdge = 14 / scale;

    if (table.type === 'rectangular') {
      // For rectangular tables: top/bottom and left/right ends
      // Derive current states the same way as planner logic
      // Prefer explicit flags if present
      const topFlag = table.seatPlacement?.topSide;
      const bottomFlag = table.seatPlacement?.bottomSide;
      const bothSides = table.seatPlacement?.bothSides === true;
      const oneSide = table.seatPlacement?.oneSide === true;
      const topEnabled = topFlag !== undefined ? topFlag : (bothSides || (!bothSides && oneSide === true));
      const bottomEnabled = bottomFlag !== undefined ? bottomFlag : (bothSides || (!bothSides && oneSide === false));
      const leftEnabled = !!table.seatPlacement?.leftEnd;
      const rightEnabled = !!table.seatPlacement?.rightEnd;

      // Replace toggles with +/- controls for seat counts
      const buttonSize = 14 / scale; // tighter
      const renderCounter = (x: number, y: number, side: 'top'|'bottom'|'leftEnd'|'rightEnd') => (
        <Group
          x={x}
          y={y}
          rotation={-table.rotation}
          onMouseDown={stopEvent}
          onTouchStart={stopEvent}
          onClick={stopEvent}
          onTap={stopEvent}
          onDblClick={stopEvent}
          onDblTap={stopEvent}
        >
          <Rect
            x={-buttonSize*1.2}
            y={-buttonSize/2}
            width={buttonSize*2.4}
            height={buttonSize}
            fill="rgba(255,255,255,0.95)"
            stroke="#d1d5db"
            cornerRadius={3/scale}
            onMouseDown={stopEvent}
            onTouchStart={stopEvent}
            onDblClick={stopEvent}
            onDblTap={stopEvent}
          />
          <Text
            text="-"
            x={-buttonSize*1.1}
            y={-buttonSize/2}
            width={buttonSize}
            height={buttonSize}
            align="center"
            verticalAlign="middle"
            fontSize={11/scale}
            onMouseEnter={() => { const stage = groupRef.current?.getStage(); if (stage) stage.container().style.cursor = 'pointer'; }}
            onMouseLeave={() => { const stage = groupRef.current?.getStage(); if (stage) stage.container().style.cursor = 'default'; }}
            onClick={(e) => { stopEvent(e); onAdjustSide && onAdjustSide(tableId, side, -1); }}
            onTap={(e) => { stopEvent(e); onAdjustSide && onAdjustSide(tableId, side, -1); }}
            onDblClick={stopEvent}
            onDblTap={stopEvent}
          />
          <Text
            text="+"
            x={buttonSize*0.1}
            y={-buttonSize/2}
            width={buttonSize}
            height={buttonSize}
            align="center"
            verticalAlign="middle"
            fontSize={11/scale}
            onMouseEnter={() => { const stage = groupRef.current?.getStage(); if (stage) stage.container().style.cursor = 'pointer'; }}
            onMouseLeave={() => { const stage = groupRef.current?.getStage(); if (stage) stage.container().style.cursor = 'default'; }}
            onClick={(e) => { stopEvent(e); onAdjustSide && onAdjustSide(tableId, side, +1); }}
            onTap={(e) => { stopEvent(e); onAdjustSide && onAdjustSide(tableId, side, +1); }}
            onDblClick={stopEvent}
            onDblTap={stopEvent}
          />
        </Group>
      );

      return (
        <Group>
          {renderCounter(0, -height/2 + insetFromEdge + 6/scale, 'top')}
          {renderCounter(0, height/2 - insetFromEdge - 6/scale, 'bottom')}
          {renderCounter(-width/2 + insetFromEdge + 6/scale, 0, 'leftEnd')}
          {renderCounter(width/2 - insetFromEdge - 6/scale, 0, 'rightEnd')}
        </Group>
      );
    } else if (table.type === 'square') {
      // For square tables: show +/- counters on all four sides
      const buttonSize = 14 / scale;
      const renderCounter = (x: number, y: number, side: 'top'|'bottom'|'left'|'right') => (
        <Group
          x={x}
          y={y}
          rotation={-table.rotation}
          onMouseDown={stopEvent}
          onTouchStart={stopEvent}
          onClick={stopEvent}
          onTap={stopEvent}
          onDblClick={stopEvent}
          onDblTap={stopEvent}
        >
          <Rect
            x={-buttonSize*1.2}
            y={-buttonSize/2}
            width={buttonSize*2.4}
            height={buttonSize}
            fill="rgba(255,255,255,0.95)"
            stroke="#d1d5db"
            cornerRadius={3/scale}
            onMouseDown={stopEvent}
            onTouchStart={stopEvent}
            onDblClick={stopEvent}
            onDblTap={stopEvent}
          />
          <Text
            text="-"
            x={-buttonSize*1.1}
            y={-buttonSize/2}
            width={buttonSize}
            height={buttonSize}
            align="center"
            verticalAlign="middle"
            fontSize={11/scale}
            onMouseEnter={() => { const stage = groupRef.current?.getStage(); if (stage) stage.container().style.cursor = 'pointer'; }}
            onMouseLeave={() => { const stage = groupRef.current?.getStage(); if (stage) stage.container().style.cursor = 'default'; }}
            onClick={(e) => { stopEvent(e); onAdjustSide && onAdjustSide(tableId, side, -1); }}
            onTap={(e) => { stopEvent(e); onAdjustSide && onAdjustSide(tableId, side, -1); }}
            onDblClick={stopEvent}
            onDblTap={stopEvent}
          />
          <Text
            text="+"
            x={buttonSize*0.1}
            y={-buttonSize/2}
            width={buttonSize}
            height={buttonSize}
            align="center"
            verticalAlign="middle"
            fontSize={11/scale}
            onMouseEnter={() => { const stage = groupRef.current?.getStage(); if (stage) stage.container().style.cursor = 'pointer'; }}
            onMouseLeave={() => { const stage = groupRef.current?.getStage(); if (stage) stage.container().style.cursor = 'default'; }}
            onClick={(e) => { stopEvent(e); onAdjustSide && onAdjustSide(tableId, side, +1); }}
            onTap={(e) => { stopEvent(e); onAdjustSide && onAdjustSide(tableId, side, +1); }}
            onDblClick={stopEvent}
            onDblTap={stopEvent}
          />
      </Group>
    );

      return (
        <Group>
          {renderCounter(0, -height/2 + insetFromEdge + 6/scale, 'top')}
          {renderCounter(0, height/2 - insetFromEdge - 6/scale, 'bottom')}
          {renderCounter(-width/2 + insetFromEdge + 6/scale, -height/4, 'left')}
          {renderCounter(width/2 - insetFromEdge - 6/scale, -height/4, 'right')}
        </Group>
      );
    }

    return null;
  };

  const handleClick = (e: Konva.KonvaEventObject<MouseEvent>) => {
    // Don't select table if clicking on a seat
    const target = e.target;
    
    // Check if the click was on a seat element
    // Seats are Circles with radius 24, or Text elements (guest names) inside seat Groups
    if (target.getType() === 'Circle') {
      const radius = target.getAttr('radius');
      if (radius === 24 || radius === 28) {
        // This is a seat circle (24) or highlight ring (28)
        return; // Don't select table when clicking on a seat
      }
    } else if (target.getType() === 'Text') {
      // Check if this text is inside a seat Group
      let parent: any = target.getParent();
      while (parent) {
        if (parent.getType() === 'Group') {
          // Check if this Group contains a seat Circle
          const children = parent.getChildren();
          for (const child of children) {
            if (child.getType() === 'Circle' && (child.getAttr('radius') === 24 || child.getAttr('radius') === 28)) {
              return; // This is a seat, don't select table
            }
          }
        }
        parent = parent.getParent();
      }
    }
    
    // Pass the native event to onSelect for multi-select detection
    onSelect(tableId, e.evt);
  };

  const posX = dragPos !== null ? dragPos.x : table.x;
  const posY = dragPos !== null ? dragPos.y : table.y;

  return (
    <Group
      ref={groupRef}
      x={posX}
      y={posY}
      rotation={table.rotation}
      draggable
      onClick={handleClick}
      onTap={handleClick}
      onDragStart={handleDragStart}
      onDragMove={handleDragMove}
      onDragEnd={handleDragEnd}
      onDblClick={() => onDoubleClick(tableId)}
      onDblTap={() => onDoubleClick(tableId)}
      onDragOver={(e: any) => {
        e.evt.preventDefault();
        e.evt.dataTransfer.dropEffect = 'copy';
      }}
      onDragEnter={(e: any) => {
        e.evt.preventDefault();
        e.evt.dataTransfer.dropEffect = 'copy';
      }}
    >
      {renderTableShape()}
      {renderSeats()}
      {renderSeatNumbers()}
      {renderGuestNames()}
      {renderTableLabel()}
      {renderDimensionIndicator()}
      {renderRotationIndicator()}
      {renderRotationHandle()}
      {renderResizeHandles()}
      {renderSeatToggleButtons()}
    </Group>
  );
}

function areVisualTablePropsEqual(prev: VisualTableProps, next: VisualTableProps): boolean {
  return (
    prev.tableId === next.tableId &&
    prev.table === next.table &&
    prev.isSelected === next.isSelected &&
    prev.highlightedRelatedGuests === next.highlightedRelatedGuests &&
    prev.useLocalDragPosition === next.useLocalDragPosition &&
    prev.scale === next.scale &&
    prev.reduceDropShadow === next.reduceDropShadow &&
    prev.onTableGroupRef === next.onTableGroupRef
  );
}

export default memo(VisualTable, areVisualTablePropsEqual);

